// Banner permanente de impersonación "ver como alumno" (F3 RBAC · read-only).
// ─────────────────────────────────────────────────────────────────────────
// Se muestra mientras hay una sesión de impersonación activa (token en
// sessionStorage + `identity.impersonating` confirmado por el backend). Avisa
// de que es solo lectura, indica a qué alumno se está viendo, y ofrece un botón
// "Salir" que cierra el rastro de auditoría (stopImpersonation), descarta el
// token y refresca la identidad para volver a la sesión real del admin.

import { useCallback, useState } from 'react';
import { Eye, X } from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { getImpersonation, clearImpersonation } from '../lib/impersonation';
import { stopImpersonation, isApiError } from '../lib/api';

export default function ImpersonationBanner(): JSX.Element | null {
  const { state, refreshAuth } = useAuth();
  const [leaving, setLeaving] = useState(false);

  // Solo se muestra si el backend confirma impersonación en la identidad
  // efectiva. (El token vive en sessionStorage; la confirmación viene del
  // perfil → así un token huérfano/expirado no deja el banner pegado.)
  const impersonating =
    state.status === 'authenticated' && state.identity.impersonating;

  const session = getImpersonation();
  // El email del ALUMNO objetivo es el de la sesión de impersonación (lo fija el
  // backend en la respuesta de /impersonate). NO usar `identity.email` primero:
  // el front mantiene ahí el email REAL del admin (sesión Supabase), así que
  // tomarlo mostraría al admin en vez del alumno (bug F-IMPERSONATE-BANNER-001).
  const targetEmail =
    session?.targetEmail ??
    (state.status === 'authenticated' ? state.identity.email : null) ??
    null;
  const actorEmail =
    session?.actorEmail ??
    (state.status === 'authenticated' ? state.identity.impersonated_by : null) ??
    null;

  const handleExit = useCallback(async (): Promise<void> => {
    setLeaving(true);
    const token = getImpersonation()?.token ?? null;
    // El stop se autentica con la sesión REAL del admin; pero ojo: bajo
    // impersonación el `state.identity` ES el alumno y su jwt podría no servir.
    // El backend resuelve el stop con la sesión real (cookie/JWT) del admin;
    // pasamos la identidad actual como mejor esfuerzo. Si falla, igual limpiamos
    // localmente (el token expira solo a 15 min).
    const auth = state.status === 'authenticated' ? state.identity : null;
    try {
      await stopImpersonation(token, auth);
    } catch (err) {
      // No bloquear la salida por un fallo de auditoría · log y seguir.
      // eslint-disable-next-line no-console
      console.warn('[impersonation] stop failed:', isApiError(err) ? err.detail : err);
    } finally {
      clearImpersonation();
      await refreshAuth();
      setLeaving(false);
    }
  }, [state, refreshAuth]);

  if (!impersonating) return null;

  return (
    <div
      role="alert"
      data-testid="impersonation-banner"
      className="shrink-0 border-b border-amber-500/50 bg-amber-500/20 px-4 py-2 text-amber-900 dark:text-amber-100"
    >
      <div className="mx-auto flex max-w-5xl items-center justify-center gap-3 text-xs sm:text-sm">
        <Eye size={16} strokeWidth={2.25} aria-hidden="true" className="shrink-0" />
        <span className="font-semibold uppercase tracking-wider">Vista de alumno</span>
        <span className="hidden font-medium opacity-90 sm:inline">·</span>
        <span className="truncate font-medium opacity-90">
          {session?.demoTier
            ? `alumno de prueba · ${session.demoTier.charAt(0).toUpperCase()}${session.demoTier.slice(1)}`
            : targetEmail
              ? `viendo el campus como ${targetEmail}`
              : 'modo solo lectura'}
          {actorEmail ? ` · admin: ${actorEmail}` : ''}
          {/* El alumno de prueba (demoTier) es ESCRIBIBLE (progreso real); la
              impersonación de un alumno real es solo lectura. */}
          {session?.demoTier ? ' · tu alumno de prueba' : ' · solo lectura'}
        </span>
        <button
          type="button"
          onClick={() => void handleExit()}
          disabled={leaving}
          data-testid="impersonation-exit"
          className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-700/40 bg-amber-700/10 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide transition hover:bg-amber-700/20 disabled:opacity-50"
        >
          <X size={14} strokeWidth={2.5} aria-hidden="true" />
          {leaving ? 'Saliendo…' : 'Salir'}
        </button>
      </div>
    </div>
  );
}
